import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronRight, Search } from "lucide-react";
import { drcr, useBooks } from "@/lib/books";
import { Input } from "@/components/ui/input";
import { Loaded, PageHeader } from "@/components/books/ui";

const NATURE = { asset: "Assets", liability: "Liabilities", income: "Income", expense: "Expenses" };

function matches(node, needle) {
  if (!needle) return true;
  if (node.name.toLowerCase().includes(needle)) return true;
  return (node.children || []).some((c) => matches(c, needle));
}

function Node({ node, depth, needle }) {
  const [open, setOpen] = useState(depth < 1);
  if (!matches(node, needle)) return null;
  const expanded = open || Boolean(needle);
  if (node.type === "ledger") {
    return (
      <div className="flex items-center justify-between border-b border-slate-100 py-2 pr-4 text-sm hover:bg-blue-50/40"
        style={{ paddingLeft: 20 + depth * 20 }}>
        <Link to={`/accounts/${node.guid}`} className="text-blue-700 hover:underline">{node.name}</Link>
        <span className="tabular-nums text-slate-700">{drcr(node.balance)}</span>
      </div>
    );
  }
  return (
    <>
      <button onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between border-b border-slate-100 py-2 pr-4 text-left text-sm hover:bg-slate-50"
        style={{ paddingLeft: 12 + depth * 20 }}>
        <span className="flex items-center gap-1.5 font-medium text-slate-800">
          {expanded ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronRight size={14} className="text-slate-400" />}
          {node.name}
          {depth === 0 && node.nature && (
            <span className="ml-1 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium uppercase text-slate-500">{NATURE[node.nature]}</span>
          )}
        </span>
        <span className="tabular-nums text-slate-600">{drcr(node.balance)}</span>
      </button>
      {expanded && (node.children || []).map((c) => (
        <Node key={`${c.type}-${c.guid || c.name}`} node={c} depth={depth + 1} needle={needle} />
      ))}
    </>
  );
}

export default function ChartOfAccounts() {
  const state = useBooks("/books/accounts");
  const [q, setQ] = useState("");
  const needle = q.trim().toLowerCase();
  const count = useMemo(() => {
    let n = 0;
    const walk = (x) => { if (x.type === "ledger") n += 1; (x.children || []).forEach(walk); };
    (state.data?.groups || []).forEach(walk);
    return n;
  }, [state.data]);
  return (
    <div className="max-w-5xl">
      <PageHeader kicker="Accountant" title="Chart of Accounts"
        subtitle={state.data ? `${count.toLocaleString("en-IN")} ledgers, grouped as in Tally · balances as of today` : null}>
        <div className="relative">
          <Search size={15} className="absolute left-2.5 top-2.5 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search accounts" className="w-64 pl-8" />
        </div>
      </PageHeader>
      <Loaded state={state}>
        {state.data && (
          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
            <div className="flex justify-between border-b border-slate-200 bg-slate-50/70 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              <span>Account</span><span>Balance</span>
            </div>
            {state.data.groups.map((g) => <Node key={`${g.type}-${g.guid || g.name}`} node={g} depth={0} needle={needle} />)}
          </div>
        )}
      </Loaded>
    </div>
  );
}
